import { Info } from "lucide-react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { headerKpi, LEGEND, paintOf, rangeText, SIGNAL_COPY, type CellBand } from "./signal";

/** A margin or lens cell (CLB-022): the served figure on its band, or `no data` on light grey. Nothing else sits in the cell. */
export function BandCell({ figure, entry, dim = false, live = false }: { figure: string | null; entry: CellBand | null; dim?: boolean; live?: boolean }) {
  // A cell with no figure paints No data light grey whatever C2 served for it (spec guard): never a band colour, never No target grey.
  const paint = figure == null ? paintOf({ band: null, state: "no_data", kpi_id: entry?.kpi_id ?? null }) : paintOf(entry);
  const text = figure == null || paint.state === "no_data" ? SIGNAL_COPY.noData : figure;
  return (
    <div className={dim ? "opacity-50" : undefined} aria-busy={dim ? "true" : undefined} data-preview={dim ? "pending" : live ? "live" : undefined}>
      <span className={`inline-block min-w-[4.5rem] rounded px-2 py-1 text-center text-sm font-semibold tabular-nums ${paint.ink === "white" ? "text-white" : "text-ks-ink"}`}
        style={{ background: paint.background }} data-band-state={paint.state} data-band={paint.band ?? undefined}>{text}</span>
    </div>
  );
}

/** A column header with its info mark (DEC-1369 comment 10, D2): the KPI's registered name, description and bands as C2 serves
 * them on the rows on view. A column whose rows name no KPI shows the bare header. */
export function HeaderInfo({ label, entries }: { label: string; entries: Array<CellBand | null> }) {
  const kpi = headerKpi(entries);
  if (!kpi) return <>{label}</>;
  return (
    <span className="inline-flex items-center gap-1">
      {label}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" aria-label={SIGNAL_COPY.info(label)} className="text-ks-grey-1 hover:text-ks-purple" data-nonmutating data-header-info
              onClick={(e) => e.stopPropagation()}><Info aria-hidden className="size-3.5" /></button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <div className="font-semibold">{kpi.name}</div>
            {kpi.description && <div>{kpi.description}</div>}
            {kpi.ranges.length > 0 && <div className="mt-1">{kpi.ranges.map(rangeText).join(" · ")}</div>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </span>
  );
}

/** The band legend, once above the table (UX v0.32 §9.10). */
export function BandLegend() {
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-ks-grey-1" data-band-legend>
      <span>{SIGNAL_COPY.legendLabel}</span>
      {LEGEND.map((l) => (
        <span key={l.label} className="flex items-center gap-1"><span aria-hidden className="inline-block size-3 rounded-sm" style={{ background: l.background }} />{l.label}</span>
      ))}
    </div>
  );
}
